import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAdminAuditLog } from "@/hooks/use-admin-audit-log";
import {
  useAdminAuthEvents,
  AUTH_EVENT_TYPES,
  type AuthEventFilters,
} from "@/hooks/use-admin-auth-events";
import { Download, ChevronLeft, ChevronRight, RefreshCw, X } from "lucide-react";
import { format } from "date-fns";

// ─────────────────────────────────────────────────────────────────────────────
// Admin-actions tab styling
// ─────────────────────────────────────────────────────────────────────────────
const actionTypeColors: Record<string, string> = {
  user_activated: "bg-green-500/10 text-green-500 border-green-500/20",
  user_deactivated: "bg-red-500/10 text-red-500 border-red-500/20",
  feature_flag_updated: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  feature_flag_reset: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  allowlist_user_added: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  allowlist_user_removed: "bg-gray-500/10 text-gray-500 border-gray-500/20",
};

const actionTypes = [
  { value: "all", label: "All Actions" },
  { value: "user_activated", label: "User Activated" },
  { value: "user_deactivated", label: "User Deactivated" },
  { value: "feature_flag_updated", label: "Flag Updated" },
  { value: "feature_flag_reset", label: "Flag Reset" },
  { value: "allowlist_user_added", label: "Allowlist Added" },
  { value: "allowlist_user_removed", label: "Allowlist Removed" },
];

// Auth-event tab badge styling — colour by category
const authEventTypeColor = (subtype: string): string => {
  if (subtype === "AUTH_LOGIN") return "bg-green-500/10 text-green-500 border-green-500/20";
  if (subtype === "AUTH_LOGOUT") return "bg-blue-500/10 text-blue-500 border-blue-500/20";
  if (subtype.endsWith("_FAILED") || subtype.endsWith("_TIMEOUT") || subtype.endsWith("_ERROR") || subtype.includes("LOOP")) {
    return "bg-red-500/10 text-red-500 border-red-500/20";
  }
  if (subtype.startsWith("GATE_") || subtype.startsWith("AUTH_BOOT_") || subtype.startsWith("AUTH_CALLBACK_")) {
    return "bg-amber-500/10 text-amber-500 border-amber-500/20";
  }
  return "bg-muted text-muted-foreground border-border";
};

const formatActionType = (type: string) =>
  type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

export default function AdminAuditLog() {
  const [activeTab, setActiveTab] = useState<"admin" | "auth">("admin");

  // Shared filters across both tabs
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Admin-actions tab state
  const [actionTypeFilter, setActionTypeFilter] = useState("");
  const adminLog = useAdminAuditLog();

  // Auth-events tab state
  const [authEventTypeFilter, setAuthEventTypeFilter] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const authLog = useAdminAuthEvents();

  // Initial fetches
  useEffect(() => {
    adminLog.fetchActions(actionTypeFilter || undefined, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTypeFilter]);

  useEffect(() => {
    if (activeTab !== "auth") return;
    const filters: AuthEventFilters = {};
    if (authEventTypeFilter) filters.authEventType = authEventTypeFilter;
    if (userSearch) filters.userIdOrEmail = userSearch;
    if (startDate) filters.startDate = new Date(startDate).toISOString();
    if (endDate) {
      // include the end date entirely
      const d = new Date(endDate);
      d.setHours(23, 59, 59, 999);
      filters.endDate = d.toISOString();
    }
    authLog.fetchEvents(filters, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, authEventTypeFilter, userSearch, startDate, endDate]);

  const adminTotalPages = Math.ceil(adminLog.total / adminLog.pageSize);
  const authTotalPages = Math.ceil(authLog.total / authLog.pageSize);

  const clearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  return (
    <AdminLayout
      title="Audit Log"
      description="HIPAA-grade activity history — admin actions and authentication events"
    >
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as "admin" | "auth")}>
        <TabsList>
          <TabsTrigger value="admin">Admin Actions</TabsTrigger>
          <TabsTrigger value="auth">Auth Events</TabsTrigger>
        </TabsList>

        {/* ─────────────────────────── ADMIN ACTIONS TAB ─────────────────────── */}
        <TabsContent value="admin">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <CardTitle>Admin Actions ({adminLog.total})</CardTitle>
                <div className="flex gap-2">
                  <Select
                    value={actionTypeFilter || "all"}
                    onValueChange={v => setActionTypeFilter(v === "all" ? "" : v)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      {actionTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => adminLog.fetchActions(actionTypeFilter || undefined, adminLog.page)}
                    disabled={adminLog.loading}
                  >
                    <RefreshCw className={`h-4 w-4 ${adminLog.loading ? "animate-spin" : ""}`} />
                  </Button>
                  <Button variant="outline" onClick={adminLog.exportToCsv}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {adminLog.loading && adminLog.actions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Loading audit log…</div>
              ) : adminLog.actions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No admin actions recorded</div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead>IP Address</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adminLog.actions.map(a => (
                        <TableRow key={a.id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(a.created_at), "MMM d, yyyy HH:mm")}
                          </TableCell>
                          <TableCell>{a.user_name || "Unknown"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={actionTypeColors[a.action] || ""}>
                              {formatActionType(a.action)}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            {a.details ? (
                              <Tooltip>
                                <TooltipTrigger className="text-sm text-muted-foreground underline decoration-dotted">
                                  View details
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md">
                                  <pre className="p-2 bg-muted rounded text-xs overflow-auto">
                                    {JSON.stringify(a.details, null, 2)}
                                  </pre>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {a.ip_address || <span className="text-muted-foreground">-</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {adminTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-muted-foreground">
                        Page {adminLog.page} of {adminTotalPages}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={adminLog.page <= 1}
                          onClick={() => adminLog.fetchActions(actionTypeFilter || undefined, adminLog.page - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={adminLog.page >= adminTotalPages}
                          onClick={() => adminLog.fetchActions(actionTypeFilter || undefined, adminLog.page + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────────────────────────── AUTH EVENTS TAB ─────────────────────────── */}
        <TabsContent value="auth">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <CardTitle>Auth Events ({authLog.total})</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Select
                      value={authEventTypeFilter || "all"}
                      onValueChange={v => setAuthEventTypeFilter(v === "all" ? "" : v)}
                    >
                      <SelectTrigger className="w-56">
                        <SelectValue placeholder="All auth events" />
                      </SelectTrigger>
                      <SelectContent>
                        {AUTH_EVENT_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      onClick={() =>
                        authLog.fetchEvents(
                          {
                            authEventType: authEventTypeFilter || undefined,
                            userIdOrEmail: userSearch || undefined,
                            startDate: startDate ? new Date(startDate).toISOString() : undefined,
                            endDate: endDate
                              ? (() => {
                                  const d = new Date(endDate);
                                  d.setHours(23, 59, 59, 999);
                                  return d.toISOString();
                                })()
                              : undefined,
                          },
                          authLog.page
                        )
                      }
                      disabled={authLog.loading}
                    >
                      <RefreshCw className={`h-4 w-4 ${authLog.loading ? "animate-spin" : ""}`} />
                    </Button>
                    <Button variant="outline" onClick={authLog.exportToCsv}>
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>

                {/* User + date range filters */}
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="user-search" className="text-xs">User (email or ID)</Label>
                    <Input
                      id="user-search"
                      type="text"
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      placeholder="user@example.com"
                      className="w-64"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="start-date" className="text-xs">From</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="end-date" className="text-xs">To</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  {(startDate || endDate) && (
                    <Button variant="ghost" size="sm" onClick={clearDates}>
                      <X className="h-3 w-3 mr-1" />
                      Clear dates
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {authLog.loading && authLog.events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Loading auth events…</div>
              ) : authLog.events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No auth events match these filters</div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Subtype</TableHead>
                        <TableHead>Session</TableHead>
                        <TableHead>Payload</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {authLog.events.map(e => (
                        <TableRow key={e.id}>
                          <TableCell className="whitespace-nowrap font-mono text-xs">
                            {format(new Date(e.created_at), "MMM d, yyyy HH:mm:ss")}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <div className="text-sm">{e.user_email || e.user_name || "—"}</div>
                            <div className="text-xs text-muted-foreground font-mono">{e.user_id.slice(0, 8)}…</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={authEventTypeColor(e.event_name)}>
                              {e.event_name}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={authEventTypeColor(e.auth_event_type)}>
                              {e.auth_event_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {e.session_id ? e.session_id.slice(0, 8) + "…" : "—"}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            {e.payload && Object.keys(e.payload).length > 0 ? (
                              <Tooltip>
                                <TooltipTrigger className="text-sm text-muted-foreground underline decoration-dotted">
                                  View payload
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md">
                                  <pre className="p-2 bg-muted rounded text-xs overflow-auto">
                                    {JSON.stringify(e.payload, null, 2)}
                                  </pre>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {authTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-muted-foreground">
                        Page {authLog.page} of {authTotalPages}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={authLog.page <= 1}
                          onClick={() =>
                            authLog.fetchEvents(
                              {
                                authEventType: authEventTypeFilter || undefined,
                                userIdOrEmail: userSearch || undefined,
                                startDate: startDate ? new Date(startDate).toISOString() : undefined,
                                endDate: endDate
                                  ? (() => {
                                      const d = new Date(endDate);
                                      d.setHours(23, 59, 59, 999);
                                      return d.toISOString();
                                    })()
                                  : undefined,
                              },
                              authLog.page - 1
                            )
                          }
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={authLog.page >= authTotalPages}
                          onClick={() =>
                            authLog.fetchEvents(
                              {
                                authEventType: authEventTypeFilter || undefined,
                                userIdOrEmail: userSearch || undefined,
                                startDate: startDate ? new Date(startDate).toISOString() : undefined,
                                endDate: endDate
                                  ? (() => {
                                      const d = new Date(endDate);
                                      d.setHours(23, 59, 59, 999);
                                      return d.toISOString();
                                    })()
                                  : undefined,
                              },
                              authLog.page + 1
                            )
                          }
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
